const BASKET_FEATURE = 'basket';
const PRODUCT_FETCHER_FEATURE = 'ProductFetcher';

/**
 * Adapter for Shoper Storefront JS API (useStorefront()).
 * Used automatically when window.useStorefront is available.
 */
export default class StorefrontAdapter {
    errorDemoMode = false;

    setErrorDemoMode(bool) {
        this.errorDemoMode = !!bool;
    }

    constructor() {
        this.basketUpdaterApi = null;
        this.basketProductsApi = null;
        this.productFetcherApi = null;
        this.ready = this.init();
    }

    init() {
        return new Promise((resolve, reject) => {
            if (typeof window === 'undefined' || typeof window.useStorefront !== 'function') {
                reject(new Error('useStorefront is not available'));
                return;
            }

            window.useStorefront(async ({ getApi, getApiSync, eventBus }) => {
                try {
                    this.eventBus = eventBus;
                    this.subscribeBasketEvents();
                    this.subscribeErrorsHandler();

                    this.basketUpdaterApi = await this.getApiOrRegister(
                        getApi, getApiSync, 'basketUpdaterApi', BASKET_FEATURE
                    );
                    this.basketProductsApi = await this.getApiOrRegister(
                        getApi, getApiSync, 'basketProductsApi', BASKET_FEATURE
                    );

                    if (!this.basketUpdaterApi || !this.basketProductsApi) {
                        reject(new Error('Storefront basket APIs are not available'));
                        return;
                    }

                    this.productFetcherApi = await this.getApiOrRegister(
                        getApi, getApiSync, 'ProductFetcherApi', PRODUCT_FETCHER_FEATURE
                    );

                    resolve();
                } catch (err) {
                    reject(err);
                }
            });
        });
    }

    subscribeBasketEvents() {
        if (!this.eventBus || this._eventsSubscribed) {
            return;
        }
        this._eventsSubscribed = true;
        [
            'basket.itemAddedToBasket',
            'basket.basketUpdated',
            'basket.itemRemovedFromBasket',
            'basket.unexpectedError'
        ].forEach((event) => {
            this.eventBus.on(event, ({ body }) => {
                console.log(`[StorefrontAdapter] ${event}`, body);
            });
        });
    }

    subscribeErrorsHandler() {
        if (!this.eventBus || this._errorsHandlerSubscribed) {
            return;
        }
        this._errorsHandlerSubscribed = true;
        this.eventBus.on('ErrorsHandler.errorOccurred', ({ body: errorData }) => {
            console.error('[StorefrontAdapter] ErrorsHandler.errorOccurred', errorData);
        });
    }

    addItemWithErrorSurfacing(payload) {
        return new Promise((resolve, reject) => {
            let settled = false;

            const cleanup = () => {
                this.eventBus.off('basket.unexpectedError', onError);
                this.eventBus.off('basket.basketUpdated', onBasketUpdated);
            };
            const settle = (fn, value) => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                fn(value);
            };

            const onError = ({ body }) => {
                console.error('[StorefrontAdapter] basket.unexpectedError', body);
                settle(reject, new Error('basket.unexpectedError: ' + JSON.stringify(body)));
            };

            const onBasketUpdated = ({ body }) => {
                const errors = this.extractBasketErrors(body);
                if (errors.length > 0) {
                    console.error('[StorefrontAdapter] addItem returned errors', errors);
                    settle(reject, new Error(errors.map((e) => e.message || JSON.stringify(e)).join('\n')));
                }
            };

            this.eventBus.on('basket.unexpectedError', onError);
            this.eventBus.on('basket.basketUpdated', onBasketUpdated);

            this.basketUpdaterApi.addItem(payload)
                .then((result) => {
                    const errors = this.extractBasketErrors(result);
                    if (errors.length > 0) {
                        console.error('[StorefrontAdapter] addItem returned errors', errors);
                        settle(reject, new Error(errors.map((e) => e.message || JSON.stringify(e)).join('\n')));
                        return;
                    }
                    settle(resolve, result);
                })
                .catch((err) => {
                    console.error('[StorefrontAdapter] addItem error', err);
                    settle(reject, err);
                });
        });
    }

    extractBasketErrors(result) {
        if (!result) {
            return [];
        }

        const items = Array.isArray(result) ? result : (result.invalidSections?.basketItems || []);
        if (!Array.isArray(items)) {
            return [];
        }

        return items.filter((item) => item && (item.isError === true || typeof item.message === 'string'));
    }

    async getApiOrRegister(getApi, getApiSync, name, feature) {
        let api = await getApi(name);
        if (api) {
            return api;
        }

        const featureSystemApi = getApiSync('FeatureSystemApi');
        if (featureSystemApi?.registerDynamic) {
            await featureSystemApi.registerDynamic(feature);
        }
        return getApi(name);
    }

    async addToBasket(data) {
        await this.ready;

        const { stock_id, options = {}, quantity, product_id } = data;
        const variantOptions = Object.entries(options).reduce((acc, [optionId, valueId]) => {
            acc[String(optionId)] = String(valueId);
            return acc;
        }, {});

        const resolveProductId = this.errorDemoMode ? 9999999 : product_id;
        if (this.errorDemoMode) {
            console.log('[StorefrontAdapter] errorDemoMode using wrong product_id', resolveProductId);
        }

        let variantId = stock_id;
        if (this.productFetcherApi && resolveProductId && Object.keys(variantOptions).length > 0) {
            let productVariant;
            try {
                productVariant = await this.productFetcherApi.getProductVariant(resolveProductId, { variantOptions });
            } catch (err) {
                console.error('[StorefrontAdapter] getProductVariant error', err);
                throw err;
            }
            console.log('[StorefrontAdapter] getProductVariant', { product_id: resolveProductId, variantOptions, productVariant });
            if (!productVariant?.stockId) {
                console.error('[StorefrontAdapter] getProductVariant returned null', { product_id: resolveProductId, variantOptions });
                throw new Error(`Product variant not found for product ${resolveProductId}`);
            }
            variantId = productVariant.stockId;
        }

        const payload = {
            variantId,
            quantity: quantity !== undefined ? quantity : 1,
            ...(Object.keys(variantOptions).length > 0 ? { variantOptions } : {}),
            showAddedModal: false,
            bundleItems: []
        };
        console.log('[StorefrontAdapter] addToBasket payload', payload);

        const basket = await this.addItemWithErrorSurfacing(payload);
        console.log('[StorefrontAdapter] addItem resolved basket', basket);

        const liveList = this.basketProductsApi.get() || [];
        console.log('[StorefrontAdapter] basket products after addItem', liveList);

        const matched = liveList.filter((product) => product.variantId === variantId);
        console.log(`[StorefrontAdapter] matched products for variantId ${variantId}`, matched);

        if (matched.length === 0) {
            console.error(`[StorefrontAdapter] product not added to basket for variantId ${variantId}`, { payload, liveList });
            throw new Error(`Product (variantId ${variantId}) could not be added to the basket`);
        }

        return matched;
    }

    async removeFromBasket(id) {
        await this.ready;

        const product = this.basketProductsApi.get().find((p) => p.id === id);
        if (product) {
            this.basketProductsApi.remove(product);
        }

        return product ? [product] : [];
    }

    async getBasket() {
        await this.ready;

        const list = this.basketProductsApi.get() || [];
        return {
            products: list.map((product) => ({
                id: product.id,
                product_id: product.productId,
                variant: product.name,
                ...product
            }))
        };
    }

    async getProduct(id) {
        await this.ready;

        if (!this.productFetcherApi) {
            throw new Error('ProductFetcherApi is not available');
        }

        return this.productFetcherApi.getProduct(id);
    }
}
